import * as React from "react";
import { useLocation } from "wouter";
import { ArrowLeft, History, CheckCircle2, Pause, Archive, Target, FlaskConical, RotateCcw, Calendar, ChevronRight, TrendingUp } from "lucide-react";
import { apiFetch } from "@/lib/api.js";

interface TrialSummary {
  total: number;
  adopted: number;
  reverted: number;
  lastOutcome: {
    status: string;
    spaceName: string | null;
    note: string | null;
    decidedAt: string | null;
  } | null;
}

interface ProcessTrialEntry {
  title: string;
  frequency: string;
  lastFeedback: string | null;
  lastCompletedAt: string | null;
}

interface HistoryGoal {
  id: string;
  title: string;
  description: string | null;
  activityLocationName: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  trialSummary: TrialSummary;
  processTrials: ProcessTrialEntry[];
  durationDays: number;
}

interface HistorySummary {
  totalResolved: number;
  achieved: number;
  paused: number;
  abandoned: number;
  averageDurationDays: number;
}

interface HistoryResponse {
  goals: HistoryGoal[];
  summary: HistorySummary;
  total: number;
  limit: number;
  offset: number;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  achieved: { label: "Achieved", color: "text-success-text bg-success-wash", icon: CheckCircle2 },
  paused: { label: "Paused", color: "text-warning-text bg-warning-wash", icon: Pause },
  abandoned: { label: "Let go", color: "text-body bg-clay-muted", icon: Archive },
};

export default function GoalHistoryPage() {
  const [, navigate] = useLocation();
  const [data, setData] = React.useState<HistoryResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<string>("all");

  const fetchHistory = React.useCallback(async () => {
    setLoadError(null);
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("status", filter);
      params.set("limit", "20");
      const result = await apiFetch<HistoryResponse>(`/api/organizing-goals/history?${params}`);
      setData(result);
    } catch (err: any) {
      setLoadError(err.message || "Failed to load history");
    }
    setLoading(false);
  }, [filter]);

  React.useEffect(() => { fetchHistory(); }, [fetchHistory]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-amethyst-soft border-t-primary rounded-pill animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-error-wash rounded-lg border border-clay-border text-center py-16 px-6">
        <p className="text-error-text mb-4">{loadError}</p>
        <button onClick={() => { setLoading(true); fetchHistory(); }} className="px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/organize/goals")} className="p-2.5 hover:bg-amethyst-soft rounded-md transition-colors" aria-label="Back to goals">
          <ArrowLeft className="w-5 h-5 text-body" />
        </button>
        <h1 className="text-2xl font-semibold text-ink flex items-center gap-2 font-headline tracking-[-0.01em]">
          <History className="w-6 h-6 text-primary" />Goal History
        </h1>
      </div>

      {/* Summary stats */}
      {data && data.summary.totalResolved > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard
            label="Achieved"
            value={data.summary.achieved}
            icon={<CheckCircle2 className="w-4 h-4 text-success" />}
          />
          <StatCard
            label="Paused"
            value={data.summary.paused}
            icon={<Pause className="w-4 h-4 text-warning" />}
          />
          <StatCard
            label="Let go"
            value={data.summary.abandoned}
            icon={<Archive className="w-4 h-4 text-faint" />}
          />
          <StatCard
            label="Avg. duration"
            value={`${data.summary.averageDurationDays}d`}
            icon={<Calendar className="w-4 h-4 text-primary" />}
          />
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2">
        {[
          { key: "all", label: "All" },
          { key: "achieved", label: "Achieved" },
          { key: "paused", label: "Paused" },
          { key: "abandoned", label: "Let go" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-2 rounded-pill text-[0.8125rem] font-medium transition-colors ${
              filter === tab.key
                ? "bg-primary text-white"
                : "bg-clay-muted text-body hover:bg-amethyst-soft"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Goal list */}
      {!data || data.goals.length === 0 ? (
        <div className="bg-sage-wash rounded-lg border border-clay-border p-8 text-center">
          <div className="text-4xl mb-3">📚</div>
          <h2 className="text-[1.0625rem] font-semibold text-ink mb-2">No history yet</h2>
          <p className="text-sm text-body max-w-sm mx-auto">
            When you resolve, pause, or let go of organizing goals, they'll appear here as a record of what you've learned about your home.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.goals.map((goal) => (
            <HistoryCard key={goal.id} goal={goal} onClick={() => navigate(`/organize/goals/${goal.id}`)} />
          ))}
        </div>
      )}

      {/* Insights section */}
      {data && data.goals.length >= 3 && (
        <div className="bg-amethyst-soft rounded-lg p-5 border border-clay-border">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h3 className="text-[1.0625rem] font-semibold text-amethyst-ink">Patterns</h3>
          </div>
          <div className="space-y-2 text-sm text-body">
            {data.summary.achieved > data.summary.abandoned && (
              <p>✓ Most of your goals end in success — your approach to organizing is working.</p>
            )}
            {data.summary.averageDurationDays < 21 && (
              <p>⚡ You tend to resolve goals quickly (avg {data.summary.averageDurationDays} days) — you're decisive about what works.</p>
            )}
            {data.summary.averageDurationDays >= 30 && (
              <p>🧪 Your goals take about {data.summary.averageDurationDays} days to resolve — you give trials proper time to settle.</p>
            )}
            {data.goals.some((g) => g.trialSummary.reverted > 0 && g.trialSummary.adopted > 0) && (
              <p>🔄 Some goals needed multiple attempts before finding what works — that's the point of trials!</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg border border-clay-border p-3 flex items-center gap-3">
      <div className="w-8 h-8 rounded-md bg-paper flex items-center justify-center shrink-0">{icon}</div>
      <div>
        <div className="text-lg font-semibold text-ink tabular-nums">{value}</div>
        <div className="text-[0.8125rem] text-faint">{label}</div>
      </div>
    </div>
  );
}

function HistoryCard({ goal, onClick }: { goal: HistoryGoal; onClick: () => void }) {
  const config = STATUS_CONFIG[goal.status] ?? STATUS_CONFIG.achieved;
  const Icon = config.icon;

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 rounded-lg bg-white border border-clay-border hover:border-amethyst/40 transition-colors"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${config.color}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-sm text-ink truncate">{goal.title}</div>
            {goal.description && (
              <div className="text-[0.8125rem] text-faint truncate mt-0.5">{goal.description}</div>
            )}
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className={`px-1.5 py-0.5 rounded-pill text-[0.8125rem] font-medium ${config.color}`}>
                {config.label}
              </span>
              <span className="text-[0.8125rem] text-faint flex items-center gap-1 tabular-nums">
                <Calendar className="w-3 h-3" />
                {goal.durationDays}d
              </span>
              {goal.trialSummary.total > 0 && (
                <span className="text-[0.8125rem] text-faint flex items-center gap-1">
                  <FlaskConical className="w-3 h-3" />
                  <span className="tabular-nums">{goal.trialSummary.adopted}</span> adopted, <span className="tabular-nums">{goal.trialSummary.reverted}</span> reverted
                </span>
              )}
              {goal.processTrials.length > 0 && (
                <span className="text-[0.8125rem] text-sage-deep flex items-center gap-1">
                  <RotateCcw className="w-3 h-3" />
                  <span className="tabular-nums">{goal.processTrials.length}</span> routine{goal.processTrials.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            {goal.trialSummary.lastOutcome?.note && (
              <div className="mt-2 text-[0.8125rem] text-body italic">
                "{goal.trialSummary.lastOutcome.note}"
              </div>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-faint shrink-0 mt-2" />
      </div>
    </button>
  );
}
